import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { SaasRemedyHint } from './SaasRemedyHint';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { classifySaasHost } from '@/Domain/helper/saasHostRouter';
import { KnownBadUrlCategory } from '@/Domain/helper/scrapeUrlClassifier';

interface KnownBadUrlNoticeProps {
  category: KnownBadUrlCategory;
  urlHost: string;
  onCorrect: () => void;
  onSwitchToManual: () => void;
}

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
};

// `info@discord.com` classifies as `email_address` with `discord.com` as its host, which would
// otherwise read as Discord intent. Every other category may carry a genuine remedy; hosts with
// none (youtube.com, google.com, …) simply fall out of `classifySaasHost` as undefined.
const CATEGORIES_WITHOUT_REMEDY: readonly KnownBadUrlCategory[] = ['email_address'];

export const KnownBadUrlNotice = ({
  category,
  urlHost,
  onCorrect,
  onSwitchToManual,
}: KnownBadUrlNoticeProps) => {
  const { t } = useTranslation('getting_started');
  // Most Discord and Instagram URLs never reach the post-scrape error view: they are stopped
  // here, before submit, by `classifyKnownBadUrl`. Offering the deferred remedy on this surface
  // is what puts it in front of the users who actually paste those links.
  const remedy = CATEGORIES_WITHOUT_REMEDY.includes(category)
    ? undefined
    : classifySaasHost(urlHost);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="flex flex-col gap-5 bg-card border border-border rounded-2xl p-6"
      data-testid="gs-known-bad-url-notice"
      role="alertdialog"
      aria-labelledby="gs-known-bad-url-notice-title"
    >
      <div className="flex flex-col gap-1.5">
        <h2 id="gs-known-bad-url-notice-title" className="text-xl font-semibold text-foreground">
          {t(`known_bad_url_notice.${category}.title`)}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(`known_bad_url_notice.${category}.description`, { host: urlHost })}
        </p>
      </div>

      {remedy && <SaasRemedyHint remedy={remedy} urlHost={urlHost} source="known_bad_url" />}

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          size="md"
          onClick={onCorrect}
          data-testid="gs-known-bad-url-notice-correct"
          className="w-full"
        >
          {t('known_bad_url_notice.correct_cta')}
        </Button>
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={onSwitchToManual}
          data-testid="gs-known-bad-url-notice-manual"
          className="!text-foreground/60 hover:!text-foreground self-center"
        >
          {t('known_bad_url_notice.secondary_cta_manual_setup')}
        </Button>
      </div>
    </motion.div>
  );
};
